import { ResponsiveLine, Serie, Point } from '@nivo/line';
import { Box, Typography } from '@mui/material';
import { RiskProfile } from '../../../../Services/API/riskProfileApi';
import { Forecast } from '../../../../Services/API/forecastApi';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import ToggleSelection from './ToggleSelection';
import {
  getXDomainForSelectedOptions,
  findForecastByTime,
  getWorkRestForecastDescription,
  getLabels,
  getWorkRestMatrix,
  getCurrentForecastValue,
  formatTemperature,
} from '../WorkRestHelpers';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import { getColorForTempValue, getGradientStops, getRiskLevelMeta } from '../RiskProfile/RiskProfileHelpers';
import { ForecastOptions } from '../../../../Constants/WorkRestConstants';
import { CustomGradientLayer } from '../../../CustomChart/CustomGradientLayer';
import { CustomNowMarker } from '../../../CustomChart/CustomNowMarker';
import { useTheme } from '@mui/material';
import { RiskLevelLabel } from '../RiskProfile/RiskLabel';
import { ProgressBar } from '../../ProgressBar';
import { WorkRestIntensity, WorkRestMatrix, WorkRestProfile } from '../../../../Services/API/workRestApi';

interface Props {
  data: Forecast[];
  riskProfile: RiskProfile;
  totalAdjustment: number;
  currentObservationValue: number;
  currentIntensity: WorkRestIntensity;
  workRestMatrix: WorkRestMatrix;
  workRestProfile: WorkRestProfile;
  durationType: 'unit' | 'percent';
}

export default function WorkRestForecastChart({
  data,
  riskProfile,
  totalAdjustment,
  currentObservationValue,
  currentIntensity,
  workRestMatrix,
  durationType,
  workRestProfile,
}: Props) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [selectedOption, setSelectedOption] = useState<ForecastOptions>(ForecastOptions.Today);
  const [clickedObservationTime, setClickedObservationTime] = useState<string | undefined>(undefined);
  const getSliceByOption = (option: ForecastOptions): Forecast[] => {
    const domain = getXDomainForSelectedOptions(data);
    const [start, end] = domain[option];
    return data.slice(start, end + 1);
  };

  const rawForecastData = useMemo(() => getSliceByOption(selectedOption), [data, selectedOption]);
  const [selectedRawForecast, setSelectedRawForecast] = useState(rawForecastData[0]);
  const selectedTemperature = getCurrentForecastValue(selectedRawForecast, workRestProfile)! + totalAdjustment;
  const [selectedWorkRestMatrix, setSelectedWorkRestMatrix] = useState(
    getWorkRestMatrix(workRestProfile, selectedTemperature, currentIntensity)
  );

  const chartData: Serie[] = useMemo(
    () => [
      {
        id: workRestProfile.primaryMetric,
        data: rawForecastData.map(d => ({
          x: d.observationTime,
          y: getCurrentForecastValue(d, workRestProfile)! + totalAdjustment,
        })),
      },
    ],
    [rawForecastData, totalAdjustment]
  );
  const selectedTime = clickedObservationTime ?? (chartData[0].data[0]?.x as string);
  const adjustedObservationValue = formatTemperature(selectedTemperature);

  const allAdjusted = useMemo(() => 
    data.reduce<number[]>((acc, d) => {
      const val = getCurrentForecastValue(d, workRestProfile);
      if (typeof val === 'number') acc.push(val + totalAdjustment);
      return acc;
    }, []),
    [data, totalAdjustment, workRestProfile]
  );  
  const fixedMinY = Math.floor(Math.min(...allAdjusted));
  const fixedMaxY = Math.ceil(Math.max(...allAdjusted));  

  const visibleLevels = useMemo(() => {
    if (!riskProfile) {
      return [
        {
          id: -1,
          name: 'PWDefault',
          riskProfileId: -1,
          description: 'Perry Weather Default Risk Level',
          max: fixedMaxY,
          color: isDark ? theme.palette.primary.light : theme.palette.primary.main,
          colorSecondary: isDark ? theme.palette.primary.main : theme.palette.primary.light,
        },
      ];
    }
    return [...riskProfile.riskLevels].sort((a, b) => a.max - b.max);
  }, [riskProfile, isDark, theme.palette.primary]);

  //X time info
  const xValues = chartData[0].data.map(d => d.x);
  const tickValues = xValues.filter((_, index) => index % 3 === 0);

  //Y temp info
  const yTickValues = useMemo(() => {
    const ticks = [];
    const start = Math.ceil(fixedMinY / 5) * 5;
    const end = Math.floor(fixedMaxY / 5) * 5;

    for (let i = start; i <= end; i += 5) {
      ticks.push(i);
    }
    return ticks;
  }, [fixedMinY, fixedMaxY]);

  const {
    name: riskLevelName,
    color: primaryColor,
    colorSecondary: secondaryColor,
  } = riskProfile ? getRiskLevelMeta(riskProfile.riskLevels, selectedTemperature) : {};

  const handleOptionChange = (newOption: ForecastOptions) => {
    setSelectedOption(newOption);

    const newFilteredData = getSliceByOption(newOption);
    if (newFilteredData.length > 0) {
      const newSelectedTime = String(newFilteredData[0].observationTime);

      setClickedObservationTime(newSelectedTime);
      setMarkerPosition(null);
      const forecastClicked = newFilteredData.find(f => moment(f.observationTime).isSame(newSelectedTime));
      setSelectedRawForecast(forecastClicked!);
    }
  };

  const onChangeTimeOption = (point: Point) => {
    const clickedTime = point.data.x as string;
    const forecastClicked = findForecastByTime(rawForecastData, clickedTime);
    const selectedTemperature = (getCurrentForecastValue(forecastClicked!, workRestProfile) ?? 0) + totalAdjustment;
    const updatedMatrix = getWorkRestMatrix(workRestProfile, selectedTemperature, currentIntensity);

    setClickedObservationTime(clickedTime);
    setSelectedRawForecast(forecastClicked!);
    setMarkerPosition(point);
    setSelectedWorkRestMatrix(updatedMatrix ?? workRestMatrix);
  };

  const [markerPosition, setMarkerPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  //Change cursor icon
  useEffect(() => {
    const svgOverlay = document.querySelector('svg > g > g > rect') as HTMLElement;
    if (svgOverlay) {
      svgOverlay.style.cursor = isDragging ? 'grabbing' : 'grab';
      svgOverlay.style.pointerEvents = 'all';
    }
  }, [isDragging]);

  useEffect(() => {
    setSelectedWorkRestMatrix(getWorkRestMatrix(workRestProfile, selectedTemperature, currentIntensity));
  }, [totalAdjustment, currentIntensity, workRestProfile, selectedTemperature]);

  const { leftLabel, rightLabel } = getLabels(workRestProfile, selectedWorkRestMatrix, durationType);

  return (
    <Box>
      <Box display={'flex'} flexDirection={'row'} gap={1} mb={2}>
        <StackedLineChartIcon fontSize='small'/>
        <Typography fontWeight={'bold'}>3-Day Forecast</Typography>
      </Box>
      <Box height={350} mb={7} width='100%'>
        <ToggleSelection
          options={[
            { key: ForecastOptions.Today, value: 'Today' },
            { key: ForecastOptions.Tomorrow, value: 'Tomorrow' },
            { key: ForecastOptions.DayAfterTomorrow, value: moment().add(2, 'days').format('dddd') },
          ]}
          selected={selectedOption}
          onChange={e => handleOptionChange(e as ForecastOptions)}
        />
        <Box
          height={350}
          mb={7}
          width='100%'
          onMouseDown={() => setIsDragging(true)}
          onMouseUp={() => setIsDragging(false)}>
          <ResponsiveLine
            data={chartData}
            margin={{ top: 30, right: 10, bottom: 50, left: 40 }}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', stacked: false, min: fixedMinY, max: fixedMaxY }}
            axisBottom={{
              legend: '',
              tickRotation: 0,
              tickSize: 5,
              tickPadding: 5,
              tickValues,
              format: value => moment(value).format('h A'),
            }}
            axisLeft={{
              format: value => `${value}°F`,
              tickValues: yTickValues,
              tickPadding: 8
            }}
            enableArea={false}
            areaOpacity={1}
            curve='monotoneX'
            colors={['rgba(255, 255, 255, 0.9)']}
            pointSize={5}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            useMesh={true}
            layers={[
              props => (
                <CustomGradientLayer
                  innerHeight={props.innerHeight}
                  innerWidth={props.innerWidth}
                  series={props.series}
                  gradientStops={getGradientStops(visibleLevels, [fixedMinY, fixedMaxY])}
                  getColorForValue={(value: any) => getColorForTempValue(value, visibleLevels)}
                />
              ),
              props => (
                <CustomNowMarker
                  innerHeight={props.innerHeight}
                  innerWidth={props.innerWidth}
                  markerPosition={markerPosition}
                  selectedValue={selectedTime}
                  series={props.series}
                />
              ),
              'grid',
              'markers',
              'axes',
              'areas',
              'points',
              'slices',
              'mesh',
              'legends',
            ]}
            theme={{
              axis: {
                ticks: {
                  text: { fill: theme.palette.text.primary },
                },
                legend: {
                  text: { fill: theme.palette.text.primary },
                },
              },
              grid: {
                line: {
                  stroke: '#ffffff33',
                },
              },
            }}
            gridYValues={undefined}
            enableGridX={false}
            enableGridY={true}
            onClick={onChangeTimeOption}
            onMouseMove={(point: Point) => {
              if (!isDragging) return;
              if (!point) return;

              onChangeTimeOption(point);
            }}
            tooltip={() => null}
            sliceTooltip={() => null}
          />
        </Box>
      </Box>

      <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
        <Box display='flex' flexDirection='column' alignItems='start'>
          <Typography variant='h5' fontWeight='bold'>
            {adjustedObservationValue}
          </Typography>
          <Typography variant='body2' color='textSecondary'>
            Adjusted {workRestProfile.primaryMetric} at {moment(clickedObservationTime).format('h:mm A')}
          </Typography>
        </Box>

        {riskProfile && riskLevelName && (
          <RiskLevelLabel primaryColor={primaryColor} secondaryColor={secondaryColor} riskLevelName={riskLevelName} />
        )}
      </Box>

      <Typography mb={2}>
        {getWorkRestForecastDescription(
          getCurrentForecastValue(selectedRawForecast, workRestProfile),
          totalAdjustment,
          currentIntensity,
          selectedWorkRestMatrix,
          rawForecastData,
          riskProfile,
          workRestProfile
        )}
      </Typography>
      <ProgressBar
        progress={selectedWorkRestMatrix?.workPercent ?? workRestMatrix.workPercent}
        leftLabel={leftLabel}
        rightLabel={rightLabel}
        primaryColor={{ background: '#B2EDFF', text: '#125D6E' }}
        secondaryColor={{ background: '#036980', text: '#A9E5F7' }}
      />
      <Box display='flex' justifyContent='flex-end' alignItems='center' gap={1} mt={2} paddingRight={2}>
        <Box width={10} height={10} borderRadius='50%' bgcolor='#B2EDFF' />
        <Typography>Work</Typography>
        <Box width={10} height={10} borderRadius='50%' bgcolor='#036980' />
        <Typography>Rest</Typography>
      </Box>
    </Box>
  );
};
